/**
 * AI Tap Finder - Customer-facing chat interface for beer discovery
 * A floating chat widget that helps customers find their perfect beer
 */

import { useState, useRef, useEffect, memo } from 'react';
import { MessageCircle, X, Send, Sparkles, Beer, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';


// Quick suggestion chips for common queries
const QUICK_SUGGESTIONS = [
  "Something hoppy",
  "Light and refreshing",
  "Dark and malty",
  "Fruity and sweet",
  "Surprise me!",
  "Low ABV options",
  "Best for winter"
];

// Message bubble component
const MessageBubble = memo(function MessageBubble({ message, isUser }) {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-amber-500 text-black rounded-br-sm'
            : 'bg-zinc-800 text-zinc-100 rounded-bl-sm'
        }`}
      >
        {!isUser && message.isLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-zinc-400">Finding your perfect pour...</span>
          </div>
        ) : (
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
        )}
      </div>
    </div>
  );
});

// Beer recommendation card
const BeerRecommendation = memo(function BeerRecommendation({ beer, onSelect }) {
  return (
    <Card 
      className="bg-zinc-800/80 border-zinc-700 p-3 hover:border-amber-500/50 transition-all cursor-pointer"
      onClick={() => onSelect?.(beer)}
      data-testid={`beer-recommendation-${beer.tap}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-amber-500 font-bold text-sm">{beer.tap}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white text-sm truncate">{beer.name}</h4>
          <p className="text-zinc-400 text-xs">{beer.brewery} • {beer.style}</p>
          {beer.why_perfect && (
            <p className="text-zinc-300 text-xs mt-2 line-clamp-2">{beer.why_perfect}</p>
          )}
          {beer.confidence && (
            <Badge 
              variant="outline" 
              className={`mt-2 text-xs ${
                beer.confidence === 'perfect_match' 
                  ? 'border-green-500/50 text-green-400'
                  : beer.confidence === 'great_fit'
                  ? 'border-amber-500/50 text-amber-400'
                  : 'border-zinc-500/50 text-zinc-400'
              }`}
            >
              {beer.confidence === 'perfect_match' ? '✨ Perfect Match' : 
               beer.confidence === 'great_fit' ? '👍 Great Fit' : '🍺 Worth Trying'}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
});

// Main AI Tap Finder component
function AITapFinder({ isOpen, onClose, onOpen }) {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      text: "Hey there! 👋 I'm your AI bartender. Tell me what you're in the mood for and I'll help you find the perfect beer from our tap wall!",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [followUp, setFollowUp] = useState('');
  const [funFact, setFunFact] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, recommendations]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      text: text.trim(),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setRecommendations([]);
    setFollowUp('');
    setFunFact('');

    // Add loading message
    const loadingId = 'loading-' + Date.now();
    setMessages(prev => [...prev, { id: loadingId, isUser: false, isLoading: true }]);

    try {
      const response = await fetch(`/ai-assistant/tap-finder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: text.trim() })
      });

      const data = await response.json();

      // Remove loading message
      setMessages(prev => prev.filter(m => m.id !== loadingId));

      if (data.success) {
        // Add AI response
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text: data.greeting || "Here's what I found for you!",
          isUser: false,
          timestamp: new Date()
        }]);

        setRecommendations(data.recommendations || []);
        setFollowUp(data.follow_up || '');
        setFunFact(data.fun_fact || '');
      } else {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text: "Sorry, I couldn't find recommendations right now. Try asking in a different way!",
          isUser: false,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('AI Tap Finder error:', error);
      setMessages(prev => prev.filter(m => m.id !== loadingId));
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        text: "Oops! Something went wrong. Please try again.",
        isUser: false,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickSuggestion = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleBeerSelect = (beer) => {
    // Could navigate to beer details or add more info
    sendMessage(`Tell me more about ${beer.name}`);
  };

  if (!isOpen) {
    return (
      <Button
        onClick={onOpen}
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-600 text-black shadow-lg shadow-amber-500/25 hover:scale-110 transition-all"
        data-testid="ai-tap-finder-toggle"
      >
        <MessageCircle className="w-6 h-6" />
      </Button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-[380px] max-w-[calc(100vw-48px)] animate-in slide-in-from-bottom-5 duration-300" data-testid="ai-tap-finder-chat">
      <Card className="bg-zinc-900 border-zinc-800 shadow-2xl shadow-black/50 overflow-hidden flex flex-col h-[600px] max-h-[calc(100vh-100px)]">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-black text-sm">AI Tap Finder</h3>
              <p className="text-black/70 text-xs">Find your perfect pour</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-black/70 hover:text-black hover:bg-white/20 h-8 w-8"
            data-testid="ai-tap-finder-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-1">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} isUser={message.isUser} />
            ))}

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-zinc-500 text-xs uppercase tracking-wide flex items-center gap-1">
                  <Beer className="w-3 h-3" /> Recommendations
                </p>
                {recommendations.map((beer, idx) => (
                  <BeerRecommendation 
                    key={idx} 
                    beer={beer} 
                    onSelect={handleBeerSelect}
                  />
                ))}
              </div>
            )}

            {/* Fun fact */}
            {funFact && (
              <div className="mt-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <p className="text-amber-400 text-xs">
                  <span className="font-semibold">🍺 Fun fact:</span> {funFact}
                </p>
              </div>
            )}

            {/* Follow-up question */}
            {followUp && !isLoading && (
              <div className="mt-3">
                <p className="text-zinc-400 text-sm italic">{followUp}</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Quick suggestions */}
        {messages.length <= 2 && !isLoading && (
          <div className="px-4 pb-2">
            <p className="text-zinc-500 text-xs mb-2">Quick suggestions:</p>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_SUGGESTIONS.slice(0, 4).map((suggestion) => (
                <Button
                  key={suggestion}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickSuggestion(suggestion)}
                  className="text-xs h-7 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-amber-400"
                  data-testid={`quick-suggestion-${suggestion.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-3 border-t border-zinc-800 bg-zinc-900/80">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="What are you in the mood for?"
              disabled={isLoading}
              className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-amber-500"
              data-testid="ai-tap-finder-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="bg-amber-500 hover:bg-amber-600 text-black px-3"
              data-testid="ai-tap-finder-send"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default memo(AITapFinder);
